import { Component, Vue } from 'vue-property-decorator';
import DataTable from '@/components/data-table/DataTable';
import { Order } from '@/models/Order';
import { Contact as ContactModel } from '@/models/Contact';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import { customFilter } from '../Users/Users';
import OrderStatusOptions, { StatusOptions } from "@/support/OrderStatus";
import StyledBox, { StyledBoxOptions } from "@/support/StyledBoxType";
import { currency, formatDate as format, sortObjectsArrayByProperty } from '@/support/String';
import { DateTime } from 'luxon';
import { Options } from '@/components/mi-dialog/MiDialog';
import UpdateOrderDialog from '@/views/Orders/UpdateOrderDialog';
import { RoleEnum } from '@/models/User';

@Component<OrderDataTable>({
  components: {
    UpdateOrderDialog,
  },
  filters: {
    formatDate(date: string) {
      return !date ? '-' : format(date, 'dd-LL-yyyy');
    },
    formatTime(time: string) {
      return !time ? '' : format(time, 'HH:mm');
    }
  },
  props: {

    containerSize: {
      type: String,
      default: 'large'
    }
  },
  computed: {
    /**
     * @function containerContextCSSClass
     *
     * compares a facultative prop containerSize to the string 'large'
     * if the containerSize is not large, dattables should not go table-layout:fixed.
     *
     * @returns string
     */
    containerContextCSSClass(): string {
      return this.containerSize === 'large'
        ? "table-in-large-container"
        : "table-in-small-container"
    }

  }
})
export default class OrderDataTable extends Vue {
  public $pageTitle = 'OrderDataTable';
  private type: string | null = null;
  protected selected: any[] = [];
  protected contact: ContactModel = new ContactModel();
  protected contactImages: any[] = [];

  /**
   * containerSize
   * datatables assumes to be wide and goes table-fixed at 1200px.
   * containerSize !== large cancels this through the computer property
   * containerContextCSSClass
   *
   * @protected
   * @type {string}
   * @memberof OrderDataTable
   */
  protected containerSize: string = '';

  protected selectedStatus: string = '';
  protected selectedStyledBox: string = '';
  protected selectedOrder: Order | null = null;
  protected selectedType: string = '';

  protected isCreating: boolean = false;
  protected isUpdatingOrder: boolean = false;

  protected filters: customFilter = {
    type: 'custom',
    items: this.statusOptions,
  };

  public mounted() {
    // @ts-ignore
    this.type = this.$route.meta.type;
    if (this.type === 'Suppliers') this.type = 'Contacts';
  }

  public dropdown() {
    return this.tableOptions.dropdown;
  }

  protected dateTime(date: string) {
    if (!date || date === "0000-00-00 00:00:00") {
      return "N/A";
    }

    return DateTime.fromSQL(date).toFormat("dd-LL-yyyy");
  }

  public refreshTable() {
    this.isUpdatingOrder = false
    this.orderDatatable ? this.orderDatatable.refresh() : null;
  }

  protected close() {
    this.$emit('input', false);
  }

  protected createInvoice() {
    if (!this.selectedOrder) {
      return;
    }

    const payload = {
      orders: [this.selectedOrder.id],
    }

    new Order()
      .createInvoice(payload)
      .then(() => {
        this.refreshTable();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  get deleteDialogOptions(): Options {
    return {
      title: (this.selectedOrder as Order).id || this.$t('dialogOptions.confirmation').toString(),
      text: 'Weet je zeker dat je deze order wilt verwijderen?',
      type: 'warning',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.delete').toString(),
          action: () => {
            if (!this.selectedOrder) {
              return;
            }

            new Order()
              .deleteOrder(this.selectedOrder.id as string)
              .then((response: any) => {
                if (response) {
                  this.orderDatatable ? this.orderDatatable.refresh(true) : null;
                  this.selectedOrder = null;
                }
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          },
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
          action: () => {
            this.selectedOrder = null;
          },
        },
      },
    };
  }

  get dialogInvoiceOptions(): Options {
    return {
      title: this.$t('dialogOptions.confirmation').toString(),
      text: 'Weet je zeker dat je deze order wilt invoicen?',
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Invoicen',
          action: () => {
            this.createInvoice();
          },
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
          action: () => {
            this.selectedOrder = null;
          },
        },
      },
    };
  }

  get dialogManyInvoiceOptions(): Options {
    const orders = this.selected.filter((order: Order) => {
      return order.status === 'checkout';
    });

    return {
      title: 'Orders invoicen',
      text: `${orders.length} van de ${this.selected.length} worden geinvoiced`,
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Invoicen',
          action: () => {
            const ordersId = orders.map((order: Order) => {
              return order.id;
            })

            const payload = {
              orders: ordersId,
            }

            new Order()
              .createInvoice(payload)
              .then(() => {
                this.refreshTable();
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          },
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
          action: () => {
            this.selectedOrder = null;
          },
        },
      },
    };
  }

  public get styledBoxOptions(): StyledBoxOptions[] {
    return StyledBox.styledBoxOptions;
  }

  public get statusOptions(): StatusOptions[] {
    return sortObjectsArrayByProperty(OrderStatusOptions.statusOptions, "value");
  }

  protected get orderDatatable(): DataTable | null {
    const dataTable = this.$refs.orderDatatable;
    if (dataTable) {
      return (dataTable as DataTable);
    }

    return null;
  }

  get tableOptions() {
    const sourceMap: any = {
      Contacts: {
        name: {
          singular: 'Order',
          plural: this.$tc('menu.orders')
        },
        sort: {
          key: 'created_at',
          order: 'DESC',
        },
        headers: [
          {
            text: this.$tc("tableHeaders.orderNumber.title"),
            value: 'peoplevox_id',
            class: 'linkable',
            sortable: {
              key: 'status',
              order: 'ASC',
            },
            transform: (value: string) => {
              return value ? value : '-';
            },
            action: '{orderUrl}',
          },
          {
            text: this.$tc("tableHeaders.client.title"),
            class: 'linkable',
            value: 'user_name',
            action: (order: Order) => {
              if (!order || !order.user) {
                return;
              }

              this.$router.push(`/contacten/${order.user.id}`);
            },
            sortable: {
              key: 'user_name',
              order: 'ASC',
            },
          },
          {
            text: this.$tc("tableHeaders.type.title"),
            value: 'box_type',
            sortable: {
              key: 'box_type',
              order: 'ASC',
            },
            transform: (value: string) => {
              const styledBox = this.styledBoxOptions.find((styledBox: StyledBoxOptions) => {
                return value === styledBox.value;
              });

              if (styledBox) {
                return (styledBox as StyledBoxOptions).text;
              }

              return '';
            }
          },
          {
            text: this.$tc("tableHeaders.quantity.title"),
            value: 'quantity',
            sortable: {
              key: 'quantity',
              order: 'ASC',
            },
          },
          {
            text: this.$tc("tableHeaders.quantitySold.title"),
            value: "sold_items",
            class: "qt-short",
            sortable: {
              key: "sold_items",
              order: "ASC"
            }
          },
          {
            text: this.$tc("tableHeaders.netSales.title"),
            value: "total_without_vat",
            sortable: {
              key: "total_without_vat",
              order: "ASC"
            },
            transform: (value: number) => {
              return value ? currency(value) : "";
            }
          },
          {
            text: this.$tc("tableHeaders.status.title"),
            value: 'status',
            transform: (value: string) => {
            const capitalizedStatus = value.charAt(0).toUpperCase() + value.slice(1);
            return `<span class="statusPill statusPill--${value}">${capitalizedStatus}</span>`;
            },
            sortable: {
              key: 'status',
              order: 'ASC',
            },
          },
          {
            text: this.$tc("tableHeaders.createdAt.title"),
            value: 'created_at',
            class: 'text-right',
            transform: (date: string) => {
              return this.dateTime(date);
            },
            sortable: {
              key: 'created_at',
              order: 'ASC',
            },
          },
          {
            text: this.$tc("tableHeaders.finalized.title"),
            value: 'finalized_at',
            class: 'text-right',
            sortable: {
              key: 'finalized_at',
              order: 'ASC',
            },
            transform: (date: string) => {
              return this.dateTime(date) || '-';
            },
          },
          {
            text: this.$tc("tableHeaders.picked.title"),
            value: 'picked_at',
            class: 'text-right',
            sortable: {
              key: 'picked_at',
              order: 'ASC',
            },
            transform: (date: string) => {
              return this.dateTime(date);
            },
          },
          {
            text: this.$tc("tableHeaders.dispatched.title"),
            value: 'dispatched_at',
            class: 'text-right',
            sortable: {
              key: 'dispatched_at',
              order: 'ASC',
            },
            transform: (date: string) => {
              return this.dateTime(date);
            },
          },
          {
            text: this.$tc("tableHeaders.returned.title"),
            value: 'returned_at',
            class: 'text-right',
            sortable: {
              key: 'returned_at',
              order: 'ASC',
            },
            transform: (date: string) => {
              return this.dateTime(date);
            },
          },
          {
            text: this.$tc("tableHeaders.stylist.title"),
            value: 'stylist_name',
            sortable: {
              key: 'stylist_name',
              order: 'ASC',
            },
          },
        ],
        actions: [
          {
            type: 'view',
            label: 'view',
            icon: 'remove_red_eye',
            action: '{orderUrl}',
            tooltip: this.$tc("orders.checkOrder"),
            visible: (datatable: DataTable, order: Order) => {
              return order.status !== 'cancelled';
            },
          },
          {
            type: 'invoice',
            label: 'invoice',
            icon: 'euro_symbol',
            tooltip: 'Invoice order',
            action: (datatable: DataTable, order: Order) => {
              if (!order) {
                return;
              }
              this.selectedOrder = order;
              this.$store.dispatch('openDialog', this.dialogInvoiceOptions);
            },
            visible: (datatable: DataTable, order: Order) => {
              return this.$store.state.Auth.canViewInvoices() && (order.status === "checkout");
            }
          },
          {
            type: 'delete',
            label: 'delete',
            icon: 'delete',
            tooltip: 'Verwijder order',
            action: (datatable: DataTable, order: Order) => {
              this.selectedOrder = order;
              this.$store.dispatch('openDialog', this.deleteDialogOptions);
            },
            visible: (datatable: DataTable, order: Order) => {
              if (order.status === 'inactive') {
                return true;
              }

              // An admin can always delete an active order
              // A stylist only if it belongs to him/her
              if (order.status === 'active') {
                if (!this.$store || !this.$store.state || !this.$store.state.Auth) {
                  return false;
                }

                if (this.$store.state.Auth.hasRole([RoleEnum.ADMINISTRATOR, RoleEnum.ROOT])) {
                  return true;
                }

                if (order.stylist_id === this.$store.state.Auth.id) {
                  return true;
                }
              }

              return false;
            }
          },
        ],
        dropdown: [
          {
            text: 'Orders invoicen',
            icon: 'euro_symbol',
            action: () => {
              this.$store.dispatch('openDialog', this.dialogManyInvoiceOptions);
            },
          },
          {
            text: 'Update status',
            icon: 'update',
            action: () => {
              this.isUpdatingOrder = true;
              this.selectedType = 'status';
            },
          },
          {
            text: 'Update type',
            icon: 'inbox',
            action: () => {
              this.isUpdatingOrder = true,
                this.selectedType = 'box_type'
            },
          },
        ],
        model: new Order().include(['user', 'order-items']).filter({ 'status': this.selectedStatus, 'box_type': this.selectedStyledBox, 'me': 1, user: this.$route.params.id }),
      },
    };
    return sourceMap[this.type as string];
  }
}
